import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { GlobalComponent } from '../../shared/global';
import {
  FareBreakDown as FareBreakDownUpSell,
  IUpSell,
} from '../../api/api-checkout/models/rq-checkout-up-sell';
import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { FlightItinerary } from '../../public/booking/interfaces';
import { ExchangeRate } from '../../api/api-checkout/models/rq-checkout-search';
import { StorageService } from '../storage';
import { buildBasePayload, buildCaller } from '../../shared/utils';
import { NormalizeItem, NormalizeUpsell } from '../../shared/models/upsell/normalize-upsell';
import { NmBaseResponse } from '../../shared/models/base/nm-base-response';

@Injectable({
  providedIn: 'root',
})
export class UpsellService {
  private upsells = new BehaviorSubject<IUpSell[]>([]);
  upsells$ = this.upsells.asObservable();

  private selectedUpsell = new BehaviorSubject<IUpSell | null>(null);
  selectedUpsell$ = this.selectedUpsell.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageService
  ) {}

  setUpsellList(value: IUpSell[]) {
    GlobalComponent.upSellGroup = value;
    this.upsells.next(value);
  }

  getUpsellListValue() {
    return this.upsells.value;
  }

  setSelectedUpsell(value: IUpSell | null) {
    if (value) this.setPricingValuesByUpSell(value);
    this.selectedUpsell.next(value);
  }

  getDetailPricingByUpsell(upsell: IUpSell) {
    return this.setPricingValuesByUpSell(upsell);
  }

  getSelectedUpsell(): IUpSell | null {
    return this.selectedUpsell.value;
  }

  fetchUpsellsByGroup(
    itinerary?: FlightItinerary,
    exchangeRate?: ExchangeRate,
    token?: string
  ): Observable<IUpSell[]> {
    const url = `${environment.urlApiMotorVuelos}/mv/up-sell`;

    const group = itinerary?.flight ?? GlobalComponent.appGroupSeleted;
    const totalPay =
      itinerary?.flight?.detailPricing?.totalPay ?? GlobalComponent.detailPricing.totalPay;
    const segmentSelected = itinerary?.segments || GlobalComponent.segmentSelected;
    const rate = exchangeRate ?? GlobalComponent.appExchangeRate;
    const tokenAuth = token ?? GlobalComponent.tokenMotorVuelo;
    const userEmail = this.storageService.getItem<any>('usuario')?.email ?? '';

    const body = {
      group,
      groupId: group.id,
      segmentSelected,
      brandedFareName: '',
      exchangeRate: rate,
      email: userEmail,
      totalFare: +totalPay.toFixed(2),
      gds: group.gds.idGDS,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenAuth}`,
    });

    return this.http.post<IUpSell[]>(url, body, { headers }).pipe(
      tap((response) => {
        if (response.length) {
          this.setUpsellList(response);
          GlobalComponent.upSellGroup = response;
          this.setSelectedUpsell(response[0]);
          GlobalComponent.appBooking.brandedFareName = response[0].name;
        }
      }),
      map((upsells) =>
        upsells.map((item) => {
          const equipaje = item.fareBreakDowns[0].equipaje.equipaje;
          return {
            ...item,
            includeHandBagDep: !!equipaje[0]?.cabina,
            includesHoldBagDep: !!equipaje[0]?.piezas,
            includeHandBagRet: group.returns ? !!equipaje[1]?.cabina : false,
            includesHoldBagRet: group.returns ? !!equipaje[1]?.piezas : false,
          };
        })
      )
    );
  }

  private setPricingValuesByUpSell(upsell: IUpSell | null) {
    let taxes = 0;
    let totalPay: number;
    let baseFareADT = 0;
    let baseFareINF = 0;
    let baseFareCNN = 0;
    let baseFareTotal = 0;

    upsell?.fareBreakDowns.forEach((item: FareBreakDownUpSell) => {
      if (item.equivalentCode == 'ADT') baseFareADT = item.baseFare;

      if (item.equivalentCode == 'CNN') baseFareCNN = item.baseFare;

      if (item.equivalentCode == 'INF') baseFareINF = item.baseFare;

      baseFareTotal += item.baseFare * item.quantity;
      taxes +=
        item.taxes.reduce(function (acc, obj) {
          return acc + obj.fareAmount;
        }, 0) * item.quantity;
    });

    totalPay =
      baseFareTotal +
      taxes +
      GlobalComponent.detailPricing.feeNMV +
      GlobalComponent.detailPricing.feePTA;

    GlobalComponent.detailPricing = {
      ...GlobalComponent.detailPricing,
      baseFareADT,
      baseFareINF,
      baseFareCNN,
      taxes,
      totalPay,
    };
    return GlobalComponent.detailPricing;
  }

  normalizeUpsellDescriptions(upsells: IUpSell[], itinerary: FlightItinerary) {
    const url = `${environment.nmUtilsApiUrl}/normalize/upsell`;

    const items = upsells.map((upsell, index) => ({
      index: upsell.index ?? index,
      name: upsell.name,
      informationServices:
        upsell.informationServices?.map((info) => ({
          description: info.description,
          itsInclude: info.itsInclude,
        })) ?? [],
    }));

    const parameter: NormalizeUpsell = {
      gds: itinerary.flight.gds.idGDS,
      airlineCode: itinerary.flight.airline.code,
      departure: itinerary.flight.departure[0].originCity.code,
      arrival: itinerary.flight.returns?.originCity?.code ?? '',
      items,
    };

    const caller = buildCaller();
    const body = buildBasePayload(caller, parameter, itinerary.transactionId);

    return this.http.post<NmBaseResponse<NormalizeItem[]>>(url, body, {
      headers: { 'not-loading': 'true' },
    });
  }
}
